import logger from 'Logger/logger';
import { mergeWith, omit } from 'lodash';
import moment from 'moment';
import { tripInformationSelector } from 'Store/trip-information/storeHelper';
import { getAttendeeNamePlaceholder } from 'Utils/participants/participants.utils';
import type { MemberHousehold } from 'Views/booking/receipt/home-details/HomeDetailsWrapper.utils';
import 'Views/booking/receipt/home-details/HomeDetailsWrapper.utils';
import type {
  AdultAttendeeWithInfosType,
  ChildAttendeeWithInfosType,
} from 'Store/booking/booking.selector';
import 'Store/booking/booking.selector';
import { userInfoSelector } from 'Store/user-info/userInfo.selectors';
export const selectAttendeesByHouseholds = (state: ReduxState): Array<MemberHousehold> =>
  state.booking.attendees.attendeesByHouseHolds;
export type AttendeesType = {
  adults: ReadonlyArray<AdultAttendeeWithInfosType>;
  children: ReadonlyArray<ChildAttendeeWithInfosType>;
  all: ReadonlyArray<AdultAttendeeWithInfosType | ChildAttendeeWithInfosType>;
};

const findFittingChild = (
  childBirthdate: (number | null | undefined) | (string | null | undefined),
  connectedUserInfo: any,
  childrenIds: Array<string>,
) => {
  const siblinds =
    connectedUserInfo?.travelCompanions?.filter((travelCompanion) =>
      moment(childBirthdate).isSame(travelCompanion.birthdate),
    ) || [];

  if (siblinds.length === 1 && !childrenIds.includes(siblinds[0].id)) {
    childrenIds.push(siblinds[0].id);
    return siblinds[0];
  }

  return null;
};

export const formatHouseholds = (proposal: Proposal, attendeesAfterPut?: Array<any>) => {
  return proposal?.households?.length
    ? proposal.households.map((household, index) => {
        const householdAttendees = [...(household?.attendees || [])]?.map((attendee) => {
          const attendeeWithInfos = proposal?.attendees?.list?.find((att) =>
            attendee.customerId ? att.customerId === attendee.customerId : attendee.id === att.id,
          );
          const attendeeWithGmNumber =
            attendeesAfterPut?.find((att) =>
              attendee.customerId ? att.customerId === attendee.customerId : attendee.id === att.id,
            ) || {};
          return mergeWith(
            { ...attendee, ...attendeeWithGmNumber },
            attendeeWithInfos || {},
            (a, b) => a || b,
          );
        });
        return {
          all: householdAttendees,
          adults: householdAttendees?.filter((attendee) => attendee.isAdult),
          children: householdAttendees?.filter((attendee) => !attendee.isAdult) || [],
          key: index,
        };
      })
    : [
        {
          all: [],
          adults: [],
          children: [],
          key: 0,
        },
      ];
};

const attendeeLabel = (proposalAttendee): string =>
  getAttendeeNamePlaceholder(
    proposalAttendee,
    !proposalAttendee.isAdult,
    proposalAttendee.id === 'A',
  );

const getAttendees = ({
  householdAttendees,
  householdId,
  identifiedAttendees,
  connectedUserInfo,
}: {
  householdAttendees: ReadonlyArray<HouseholdAttendee>;
  householdId: number;
  identifiedAttendees: ReadonlyArray<ChildAttendeeWithInfosType | AdultAttendeeWithInfosType>;
  connectedUserInfo: any;
}): any => {
  const childrenIds = [];
  return householdAttendees.map(
    ({ id, customerId, birthdate, neolid, ...householdAttendee }, attendeeIndex) => {
      const tripInformationAttendee = identifiedAttendees?.find((att) =>
        customerId ? att.customerId === customerId || att.neolid === customerId : att.id === id,
      );
      const connectedUserInfos =
        householdAttendee.isAdult &&
        householdId === 0 &&
        attendeeIndex === 0 &&
        !!connectedUserInfo?.neolaneId
          ? {
              ...connectedUserInfo,
              neolid:
                connectedUserInfo.neolid ||
                connectedUserInfo.customerId ||
                connectedUserInfo.neolaneId,
              birthdate: connectedUserInfo.birthdate || connectedUserInfo.dateOfBirth,
              mobile: connectedUserInfo.mobilePhoneNumber,
            }
          : {};
      const child = findFittingChild(birthdate, connectedUserInfo, childrenIds) || {};
      const attendeeWithInfos = mergeWith(
        {
          birthdate,
          customerId,
          neolid: customerId,
          ...householdAttendee,
          ...connectedUserInfos,
          // important to keep the ABCD ids
          ...(!householdAttendee.isAdult ? omit(child, 'id') : {}),
          householdId,
          isChild: !householdAttendee.isAdult,
          // should be kept here to change all attendees last name in case of travel companions
          lastName:
            householdAttendee.lastName ||
            tripInformationAttendee?.lastName ||
            (!householdAttendee.isAdult ? child?.lastName : '') ||
            connectedUserInfo?.lastName,
          id,
          attendeeIndex,
        },
        tripInformationAttendee || {},
        (objValue, srcValue) => objValue || srcValue,
      );
      const lastValue = {};

      for (const [key, value] of Object.entries(attendeeWithInfos)) {
        lastValue[key] = value === null ? undefined : value;
      }

      return { ...lastValue, name: attendeeLabel(lastValue) };
    },
  );
};

const getFormattedHouseholds = ({
  households,
  tripInformationAttendees,
  connectedUserInfo,
}): Array<
  AttendeesType & {
    key: number;
  }
> => {
  return households?.map(({ adults, children }, householdId) => {
    const newAdultsList = getAttendees({
      householdAttendees: [...adults],
      householdId,
      identifiedAttendees: tripInformationAttendees ? [...tripInformationAttendees.adults] : [],
      connectedUserInfo,
    });
    const newChildrenList = children.length
      ? getAttendees({
          householdAttendees: [...children],
          householdId,
          identifiedAttendees: tripInformationAttendees ? [...tripInformationAttendees.kids] : [],
          connectedUserInfo,
        })
      : [];
    return {
      key: householdId,
      adults: [...newAdultsList],
      children: [...newChildrenList],
      all: [...newAdultsList, ...newChildrenList],
    };
  });
};

export const selectAttendeesWithInfosByHouseholds = (
  state: ReduxState,
): {
  households: Array<
    AttendeesType & {
      key: number;
    }
  >;
  attendees: {
    list: Array<any>;
    adults: Array<any>;
    children: Array<any>;
  };
} => {
  const formattedHouseholds = selectAttendeesByHouseholds(state);
  const tripInformationAttendees = tripInformationSelector(state);
  const connectedUserInfo = userInfoSelector(state) || {};

  try {
    const households = getFormattedHouseholds({
      households: formattedHouseholds,
      tripInformationAttendees,
      connectedUserInfo,
    });
    return {
      households,
      attendees: households.reduce(
        (attendees, value) => ({
          adults: attendees.adults.concat(value.adults),
          children: attendees.children.concat(value.children),
          list: attendees.list.concat(value.all),
        }),
        {
          list: [],
          adults: [],
          children: [],
        },
      ),
    };
  } catch (error) {
    logger.error(error);
    return {
      households: [],
      attendees: {
        list: [],
        adults: [],
        children: [],
      },
    };
  }
};
type AddInfosProps = {
  proposal: Proposal;
  tripInformationAttendees?: any;
  connectedUserInfo?: any;
};
type ReturnInfosProps = {
  households: Array<
    AttendeesType & {
      key: number;
    }
  >;
  attendees: {
    adults: Array<any>;
    children: Array<any>;
    list: Array<any>;
  };
};
export const addInfosToProposalAttendees = ({
  proposal,
  tripInformationAttendees,
  connectedUserInfo,
}: AddInfosProps): ReturnInfosProps => {
  try {
    const formattedHouseholds = getFormattedHouseholds({
      households: formatHouseholds(proposal),
      tripInformationAttendees,
      connectedUserInfo,
    });
    const formattedAttendees = {
      list: [],
      adults: [],
      children: [],
    };
    formattedHouseholds.map((value) => {
      formattedAttendees.adults = [...formattedAttendees.adults, ...value.adults];
      formattedAttendees.children = [...formattedAttendees.children, ...value.children];
      formattedAttendees.list = [...formattedAttendees.list, ...value.all];
    });
    return {
      households: formattedHouseholds,
      attendees: formattedAttendees,
    };
  } catch (error) {
    logger.error(error);
    return {
      households: [
        {
          all: [],
          adults: [],
          children: [],
          key: 0,
        },
      ],
      attendees: {
        list: [],
        adults: [],
        children: [],
      },
    };
  }
};
export const selectNumberOfHouseholds = (state: ReduxState): number =>
  state.booking.attendees.numberOfHouseHolds;
